import { useState, useEffect, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, ArrowDown, ArrowUp, Minus, ChevronRight, Download } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { BottomNav } from "@/components/BottomNav";
import { NameDialog } from "@/components/NameDialog";
import { MetricChart } from "@/components/MetricChart";
import {
  DEFAULT_METRICS,
  loadCustomMetrics,
  addCustomMetric,
  deleteCustomMetric,
  loadMeasurements,
  recordMeasurement,
  deleteMeasurement,
} from "@/storage/measurements";
import { ExportDialog } from "@/components/ExportDialog";

function DeltaBadge({ delta, unit }) {
  if (delta === null || delta === 0) {
    return (
      <Badge className="bg-secondary/50 text-muted-foreground border-0 rounded-full text-[10px] gap-0.5">
        <Minus className="h-2.5 w-2.5" /> 0
      </Badge>
    );
  }
  const up = delta > 0;
  return (
    <Badge
      className={`border-0 rounded-full text-[10px] gap-0.5 ${
        up ? "bg-orange-500/15 text-orange-400" : "bg-success/15 text-success"
      }`}
    >
      {up ? <ArrowUp className="h-2.5 w-2.5" /> : <ArrowDown className="h-2.5 w-2.5" />}
      {Math.abs(delta)} {unit}
    </Badge>
  );
}

export default function BodyWeightPage() {
  const [customMetrics, setCustomMetrics] = useState([]);
  const [measurements, setMeasurements] = useState({});
  const [openMetric, setOpenMetric] = useState(null);
  const [newValue, setNewValue] = useState("");
  const [addMetricOpen, setAddMetricOpen] = useState(false);
  const [newMetricName, setNewMetricName] = useState("");
  const [newMetricUnit, setNewMetricUnit] = useState("cm");
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    setCustomMetrics(loadCustomMetrics());
    setMeasurements(loadMeasurements());
  }, []);

  const metrics = useMemo(() => [...DEFAULT_METRICS, ...customMetrics], [customMetrics]);

  const summaries = useMemo(() => {
    const out = {};
    metrics.forEach((m) => {
      const entries = measurements[m.id] || [];
      const last = entries[entries.length - 1];
      const prev = entries.length > 1 ? entries[entries.length - 2] : null;
      out[m.id] = {
        last: last ? last.value : null,
        delta: last && prev ? Number((last.value - prev.value).toFixed(2)) : null,
        count: entries.length,
      };
    });
    return out;
  }, [metrics, measurements]);

  const handleRecord = () => {
    const value = parseFloat(newValue.replace(",", "."));
    if (isNaN(value)) return;
    recordMeasurement(openMetric.id, value);
    setMeasurements(loadMeasurements());
    setNewValue("");
    toast.success(`${openMetric.name} actualizado`);
  };

  const handleDeleteEntry = (index) => {
    deleteMeasurement(openMetric.id, index);
    setMeasurements(loadMeasurements());
  };

  const handleAddMetric = () => {
    const name = newMetricName.trim();
    if (!name) return;
    addCustomMetric(name, newMetricUnit.trim() || "cm");
    setCustomMetrics(loadCustomMetrics());
    setNewMetricName("");
    setNewMetricUnit("cm");
    setAddMetricOpen(false);
    toast.success("Medida añadida");
  };

  const handleDeleteMetric = (id) => {
    deleteCustomMetric(id);
    setCustomMetrics(loadCustomMetrics());
    setOpenMetric(null);
    toast.success("Medida eliminada");
  };

  const openEntries = openMetric ? measurements[openMetric.id] || [] : [];

  return (
    <div className="min-h-screen bg-background pb-28">
      <div className="mx-auto max-w-2xl px-5 pt-8">
        <div className="flex items-start justify-between mb-7">
          <div>
            <p className="text-[11px] font-medium text-primary uppercase tracking-[0.15em]">
              Control corporal
            </p>
            <h1 className="text-foreground font-bold text-2xl tracking-tight mt-1">Medidas</h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setExportOpen(true)}
            aria-label="Exportar mis datos"
            className="h-10 w-10 rounded-2xl glass-surface text-muted-foreground hover:text-foreground"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>

        <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />

        <div className="space-y-2">
          {metrics.map((metric) => {
            const s = summaries[metric.id];
            return (
              <motion.button
                key={metric.id}
                layout
                onClick={() => {
                  setOpenMetric(metric);
                  setNewValue("");
                }}
                className="w-full flex items-center gap-3 p-4 rounded-2xl glass-surface hover:bg-secondary/40 transition-colors duration-200 text-left"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground text-sm truncate">{metric.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {s.count === 0
                      ? "Sin registros"
                      : `${s.count} registro${s.count === 1 ? "" : "s"}`}
                  </p>
                </div>

                {s.last !== null && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <DeltaBadge delta={s.delta} unit={metric.unit} />
                    <span className="font-bold text-foreground tabular-nums">
                      {s.last}
                      <span className="text-[11px] text-muted-foreground font-medium ml-0.5">
                        {metric.unit}
                      </span>
                    </span>
                  </div>
                )}

                <ChevronRight className="h-4 w-4 text-muted-foreground/40 flex-shrink-0" />
              </motion.button>
            );
          })}

          <button
            onClick={() => setAddMetricOpen(true)}
            className="w-full flex items-center gap-3 p-4 rounded-2xl border border-dashed border-border/60 hover:border-primary/50 hover:bg-card/40 transition-all duration-200 text-left"
          >
            <div className="w-9 h-9 rounded-full bg-secondary/50 flex items-center justify-center flex-shrink-0">
              <Plus className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="font-semibold text-foreground text-sm">Añadir medida</p>
              <p className="text-[11px] text-muted-foreground">Crea una métrica personalizada</p>
            </div>
          </button>
        </div>
      </div>

      {/* Detalle de una métrica: registrar valor, gráfica e historial */}
      <Dialog open={!!openMetric} onOpenChange={(v) => !v && setOpenMetric(null)}>
        <DialogContent className="glass-surface rounded-3xl border-0 w-[calc(100vw-2rem)] max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader className="text-left">
            <DialogTitle className="text-foreground text-xl font-bold tracking-tight">
              {openMetric?.name}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Registra un valor nuevo o consulta tu evolución.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2">
            <Input
              type="number"
              inputMode="decimal"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRecord()}
              placeholder={`Valor en ${openMetric?.unit}`}
              autoFocus
              className="h-12 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium"
            />
            <Button
              onClick={handleRecord}
              disabled={!newValue.trim()}
              className="h-12 px-5 rounded-xl bg-primary text-primary-foreground font-bold disabled:opacity-40"
            >
              Guardar
            </Button>
          </div>

          {openEntries.length > 1 && (
            <Card className="rounded-2xl glass-surface border-0 p-4">
              <MetricChart
                data={openEntries}
                unit={openMetric?.unit}
                gradientId={`grad-${openMetric?.id}`}
              />
            </Card>
          )}

          {openEntries.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Registros
              </p>
              <AnimatePresence>
                {[...openEntries].reverse().map((entry, revIdx) => {
                  const idx = openEntries.length - 1 - revIdx;
                  return (
                    <motion.div
                      key={entry.date}
                      layout
                      exit={{ opacity: 0, x: -20 }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-secondary/20"
                    >
                      <span className="text-xs text-muted-foreground flex-1">
                        {new Date(entry.date).toLocaleDateString("es-ES", {
                          day: "numeric",
                          month: "short",
                          year: "2-digit",
                        })}
                      </span>
                      <span className="font-bold text-foreground tabular-nums text-sm">
                        {entry.value} {openMetric?.unit}
                      </span>
                      <button
                        onClick={() => handleDeleteEntry(idx)}
                        aria-label="Eliminar registro"
                        className="text-muted-foreground/40 hover:text-destructive transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          ) : (
            <p className="text-center text-xs text-muted-foreground py-6">
              Aún no hay registros de esta medida.
            </p>
          )}

          {openMetric?.custom && (
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => handleDeleteMetric(openMetric.id)}
                className="w-full rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar esta medida
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Crear métrica personalizada */}
      <Dialog open={addMetricOpen} onOpenChange={setAddMetricOpen}>
        <DialogContent className="glass-surface rounded-3xl border-0 w-[calc(100vw-2rem)] max-w-sm">
          <DialogHeader className="text-left">
            <DialogTitle className="text-foreground text-lg font-bold">Nueva medida</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Se añadirá al final de la lista.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">
                Nombre
              </Label>
              <Input
                value={newMetricName}
                onChange={(e) => setNewMetricName(e.target.value)}
                placeholder="Ej: Espalda"
                autoFocus
                className="h-12 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">
                Unidad
              </Label>
              <Input
                value={newMetricUnit}
                onChange={(e) => setNewMetricUnit(e.target.value)}
                placeholder="cm"
                className="h-12 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium"
              />
            </div>
          </div>

          <DialogFooter className="flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => setAddMetricOpen(false)}
              className="flex-1 rounded-xl glass-surface text-foreground/80"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddMetric}
              disabled={!newMetricName.trim()}
              className="flex-1 rounded-xl bg-primary text-primary-foreground font-bold disabled:opacity-40"
            >
              Añadir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NameDialog />
      <BottomNav />
    </div>
  );
}
